// Iter-194s — CEO Dashboard widget · "Status CRA équipe — semaine en cours"
//
// Displays the live count of ACTIVE non-freelance users who have no CRA entry
// AND no approved absence for the current ISO week. Mirrors the eligibility
// filter of the Friday-14h cron, so the CEO can:
//   • see exactly who didn't submit yet, with their last reminder timestamp
//   • manually trigger the reminder email for selected users (e.g. on a
//     Thursday or right after 14h05 on Friday)
//
// The widget is read-only outside of the "Relancer" buttons. Idempotent by
// default; "Force re-send" bypasses the once-per-ISO-week guard.
import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, RefreshCw, Send, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

export default function EmptyWeekCRAStatusBlock() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [force, setForce] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/empty-week-status");
      setData(r.data || null);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const empty = data?.users_empty || [];
  const total = data?.users_total ?? 0;
  const submittedCount = Math.max(0, total - empty.length);

  const toggleAll = () => {
    if (selected.size === empty.length) setSelected(new Set());
    else setSelected(new Set(empty.map((u) => u.user_id)));
  };
  const toggleOne = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };

  const remindSelected = async () => {
    if (selected.size === 0) {
      toast.error("Aucun utilisateur sélectionné");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/empty-week-status/remind", {
        user_ids: Array.from(selected),
        force,
      });
      const d = r.data || {};
      // Iter-194s — pick tone based on outcome instead of always "success".
      const summary = `${d.sent || 0} envoyé(s) · ${d.skipped || 0} skip · ${d.errors || 0} erreur(s)`;
      if ((d.sent || 0) > 0 && (d.errors || 0) === 0) {
        toast.success(`Relance envoyée — ${summary}`);
      } else if ((d.errors || 0) > 0 && (d.sent || 0) === 0) {
        toast.error(`Échec relance — ${summary}`);
      } else {
        toast.message(`Relance partielle — ${summary}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec relance");
    } finally { setSending(false); }
  };

  const remindOne = async (uid) => {
    setSending(true);
    try {
      const r = await api.post("/admin/empty-week-status/remind", {
        user_ids: [uid],
        force,
      });
      const result = r.data?.results?.[0] || {};
      if (result.status === "sent") toast.success(`Relance envoyée à ${result.email || uid}`);
      else if (result.status === "skipped") toast.message(`Skip ${uid} — ${result.reason}`);
      else toast.error(`Erreur ${uid} — ${result.reason || "?"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setSending(false); }
  };

  const allSelected = useMemo(() => empty.length > 0 && selected.size === empty.length, [empty, selected]);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="emptyweek-status-block">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <CalendarClock size={18} className="text-zinc-700" />
          <div>
            <div className="font-heading font-bold text-base text-zinc-900">CRA équipe — semaine en cours</div>
            {data && (
              <div className="text-[11px] text-zinc-500 font-mono">
                {data.iso_week} · {data.period}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="emptyweek-refresh-btn"
          className="inline-flex items-center gap-1.5 px-2.5 h-8 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Rafraîchir
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <KpiBox
          testid="emptyweek-kpi-total"
          label="Collaborateurs éligibles"
          value={total}
          tone="neutral"
        />
        <KpiBox
          testid="emptyweek-kpi-submitted"
          label="Ont saisi"
          value={submittedCount}
          tone="ok"
          icon={<CheckCircle2 size={12} />}
        />
        <KpiBox
          testid="emptyweek-kpi-empty"
          label="Semaine vide"
          value={empty.length}
          tone={empty.length === 0 ? "ok" : "warn"}
          icon={<AlertCircle size={12} />}
        />
      </div>

      {loading && !data && (
        <div className="py-6 text-center text-sm text-zinc-500">
          <Loader2 size={16} className="animate-spin inline mr-2" />
          Chargement…
        </div>
      )}

      {!loading && empty.length === 0 && (
        <div className="py-6 text-center text-sm text-zinc-600 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="emptyweek-empty-state">
          <CheckCircle2 size={20} className="inline text-emerald-600 mr-2" />
          Tout le monde a saisi des jours cette semaine ou est en absence approuvée.
        </div>
      )}

      {!loading && empty.length > 0 && (
        <div className="space-y-3">
          {/* Bulk action bar */}
          <div className="flex items-center justify-between gap-3 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-zinc-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  data-testid="emptyweek-select-all"
                  className="h-3.5 w-3.5"
                />
                Tout sélectionner ({selected.size}/{empty.length})
              </label>
              <label className="flex items-center gap-1.5 text-xs text-zinc-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  data-testid="emptyweek-force"
                  className="h-3.5 w-3.5"
                />
                Forcer (bypass idempotence)
              </label>
            </div>
            <button
              onClick={remindSelected}
              disabled={sending || selected.size === 0}
              data-testid="emptyweek-bulk-remind-btn"
              className="inline-flex items-center gap-1.5 px-3 h-8 bg-zinc-950 text-white text-xs rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Relancer ({selected.size})
            </button>
          </div>

          {/* Users list */}
          <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100" data-testid="emptyweek-users-list">
            {empty.map((u) => (
              <div
                key={u.user_id}
                className={`px-3 py-2 flex items-center gap-3 ${u.already_reminded_this_week ? "bg-zinc-50/60" : ""}`}
                data-testid={`emptyweek-row-${u.user_id}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(u.user_id)}
                  onChange={() => toggleOne(u.user_id)}
                  data-testid={`emptyweek-check-${u.user_id}`}
                  className="h-3.5 w-3.5 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate flex items-center gap-2">
                    {u.name || u.email || u.user_id}
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">
                      {u.role}
                    </span>
                    {u.already_reminded_this_week && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                        Relancé
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {u.email}
                    {u.last_reminder_at && (
                      <span className="ml-2">· dernière relance : {new Date(u.last_reminder_at).toLocaleString("fr-FR", {
                        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                      })}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => remindOne(u.user_id)}
                  disabled={sending}
                  data-testid={`emptyweek-remind-${u.user_id}`}
                  className="inline-flex items-center gap-1 px-2 h-7 border border-zinc-200 text-[11px] rounded hover:bg-zinc-50 disabled:opacity-50"
                  title="Relancer ce collaborateur"
                >
                  <Send size={11} /> Relancer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiBox({ label, value, tone = "neutral", icon, testid }) {
  const toneCls = tone === "warn"
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : tone === "ok"
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : "bg-zinc-50 border-zinc-200 text-zinc-900";
  return (
    <div className={`border rounded-md px-3 py-2 ${toneCls}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest font-mono opacity-70 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="font-mono text-2xl font-semibold leading-tight">{value}</div>
    </div>
  );
}
